import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Truck } from "lucide-react";
import { api, API, errMsg, money } from "@/lib/api";
import { Amount, Fx } from "@/components/Currency";
import { Btn, Card, Empty, Field, Input, Loading, Modal, Pill, Select, Table } from "@/components/Ui";

const STAGE_TONE = { delivered: "ok", shipped: "gold", cancelled: "bad" };
const STAGE_OPTIONS = [
  ["confirmed", "Confirmed"],
  ["manufacturing", "Manufacturing"],
  ["ready_to_ship", "Ready to ship"],
  ["shipped", "Shipped"],
  ["delivered", "Delivered"],
];

export const Timeline = ({ steps = [] }) => (
  <ol className="mt-5 flex flex-col gap-0 sm:flex-row sm:items-start" data-testid="order-timeline">
    {steps.map((s, i) => (
      <li key={s.stage} className="relative flex flex-1 gap-3 pb-6 sm:flex-col sm:pb-0"
        data-testid={`timeline-step-${s.stage}`}>
        <div className="flex sm:w-full sm:items-center">
          <span
            className={`z-10 grid h-8 w-8 shrink-0 place-items-center rounded-full border-2 transition-colors ${
              s.done
                ? "border-[#147D78] bg-[#147D78] text-white"
                : "border-[#DFE6ED] bg-white text-[#8598ac]"
            } ${s.current ? "ring-4 ring-[#147D78]/15" : ""}`}
          >
            {s.done ? <Check className="h-4 w-4" /> : <span className="text-xs font-bold">{i + 1}</span>}
          </span>
          {i < steps.length - 1 && (
            <span className={`hidden h-[2px] w-full sm:block ${
              steps[i + 1].done ? "bg-[#147D78]" : "bg-[#DFE6ED]"}`} />
          )}
          {i < steps.length - 1 && (
            <span className={`absolute left-4 top-8 h-full w-[2px] sm:hidden ${
              steps[i + 1].done ? "bg-[#147D78]" : "bg-[#DFE6ED]"}`} />
          )}
        </div>
        <div className="sm:mt-3 sm:pr-4">
          <p className={`text-sm font-bold ${s.done ? "text-[#102A43]" : "text-[#8598ac]"}`}>
            {s.label}
          </p>
          <p className="mt-0.5 text-xs text-[#667788]">
            {s.at ? new Date(s.at).toLocaleDateString(undefined,
              { day: "2-digit", month: "short", year: "numeric" }) : "Pending"}
          </p>
          {s.note && <p className="mt-1 max-w-[190px] text-xs text-[#667788]">{s.note}</p>}
        </div>
      </li>
    ))}
  </ol>
);

export function BuyerOrders() {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    api.get("/buyer/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);
  if (!orders) return <Loading label="Loading your orders…" />;
  if (!orders.length)
    return <Empty testId="buyer-orders-empty" title="No orders yet"
      sub="When you award an enquiry the order and its shipment tracking appear here." />;
  return (
    <div data-testid="buyer-orders">
      <Table head={["Order", "Supplier", "Part", "Amount", "Status", "Est. delivery", ""]}>
        {orders.map((o) => (
          <tr key={o.id} data-testid={`order-row-${o.order_number}`}>
            <td className="font-semibold text-[#102A43]">{o.order_number}</td>
            <td>{o.supplier_company_name}</td>
            <td>{o.part_number}</td>
            <td><Amount value={o.total_landed_cost} currency={o.currency} block /></td>
            <td>
              <Pill tone={STAGE_TONE[o.stage] || "neutral"} testId={`order-stage-${o.order_number}`}>
                {o.stage_label}
              </Pill>
            </td>
            <td>{o.est_delivery || "—"}</td>
            <td>
              <Link to={`/buyer/orders/${o.id}`} data-testid={`track-order-${o.order_number}`}
                className="text-xs font-bold text-[#147D78] underline">Track</Link>
            </td>
          </tr>
        ))}
      </Table>
    </div>
  );
}

export function OrderTracking() {
  const { orderId } = useParams();
  const [o, setO] = useState(null);
  useEffect(() => {
    api.get(`/buyer/orders/${orderId}`).then((r) => setO(r.data)).catch(() => setO(false));
  }, [orderId]);
  if (o === false) return <Empty testId="order-not-found" title="Order not found" />;
  if (!o) return <Loading label="Loading order…" />;

  return (
    <div data-testid="order-tracking" className="space-y-6">
      <Link to="/buyer/orders" data-testid="back-to-orders"
        className="inline-flex items-center gap-1.5 text-xs font-bold text-[#667788] hover:text-[#102A43]">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to orders
      </Link>
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#147D78]">
              {o.route === "managed" ? "Managed by ADIT" : "Direct purchase"}
            </p>
            <h2 className="mt-1 font-display text-2xl font-black tracking-tight text-[#102A43]"
              data-testid="order-number">{o.order_number}</h2>
            <p className="mt-1 text-sm text-[#667788]">
              {o.supplier_company_name} · {o.part_number} · enquiry {o.rfq_number}
            </p>
            {o.payment_terms_label && (
              <p className="mt-2 inline-flex rounded-[8px] border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-2.5 py-1 text-xs font-semibold text-[#F59E0B]"
                data-testid="order-payment-terms">
                Agreed payment terms: {o.payment_terms_label}
              </p>
            )}
          </div>
          <div className="text-right">
            <p className="font-display text-xl font-black text-[#102A43]">
              {money(o.total_landed_cost, o.currency)}
              <Fx amount={o.total_landed_cost} from={o.currency} className="ml-1.5 text-xs" />
            </p>
            <Pill tone={STAGE_TONE[o.stage] || "neutral"} testId="order-stage-pill">
              {o.stage_label}
            </Pill>
          </div>
        </div>
        <Timeline steps={o.timeline || []} />
      </Card>

      <Card>
        <h3 className="flex items-center gap-2 font-display text-base font-bold tracking-tight text-[#102A43]">
          <Truck className="h-4 w-4 text-[#147D78]" /> Shipment details
        </h3>
        <dl className="mt-4 grid gap-4 sm:grid-cols-3" data-testid="shipment-details">
          {[["Carrier", o.carrier], ["Tracking number", o.tracking_no],
            ["Estimated delivery", o.est_delivery]].map(([label, value]) => (
            <div key={label}>
              <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">
                {label}
              </dt>
              <dd className="mt-1 text-sm font-semibold text-[#102A43]">{value || "Awaiting update"}</dd>
            </div>
          ))}
        </dl>
        {o.updated_at && (
          <p className="mt-4 text-xs text-[#8598ac]">
            Last updated {new Date(o.updated_at).toLocaleString()}
          </p>
        )}
      </Card>

      <Card>
        <h3 className="font-display text-base font-bold tracking-tight text-[#102A43]">Documents</h3>
        {(o.documents || []).length === 0 ? (
          <p className="mt-3 text-sm text-[#667788]" data-testid="order-documents-empty">
            No documents attached to this order yet.
          </p>
        ) : (
          <ul className="mt-3 space-y-2" data-testid="order-documents">
            {o.documents.map((d, i) => (
              <li key={`${d.file_id}-${i}`}
                className="flex flex-wrap items-center justify-between gap-2 rounded-[10px] border border-[#DFE6ED] px-3 py-2.5">
                <span className="text-sm font-semibold text-[#102A43]">{d.filename}</span>
                <span className="text-xs text-[#667788]">{d.source}</span>
                <a href={`${API}/files/${d.file_id}`} target="_blank" rel="noreferrer"
                  data-testid={`order-doc-${d.file_id}`}
                  className="text-xs font-bold text-[#147D78] underline">Open</a>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export function SupplierOrders() {
  const [orders, setOrders] = useState(null);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/supplier/orders").then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/supplier/orders/${edit.id}/tracking`, {
        stage: edit.stage, carrier: edit.carrier || null, tracking_no: edit.tracking_no || null,
        est_delivery: edit.est_delivery || null, note: edit.note || null,
      });
      toast.success("Shipment status updated — the buyer has been notified");
      setEdit(null);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!orders) return <Loading label="Loading your orders…" />;
  if (!orders.length)
    return <Empty testId="supplier-orders-empty" title="No won orders yet"
      sub="Orders appear here when a buyer awards one of your quotations." />;

  return (
    <div data-testid="supplier-orders" className="space-y-6">
      <Card>
        <h2 className="font-display text-2xl font-black tracking-tight text-[#102A43]">Orders won</h2>
        <p className="mt-2 max-w-2xl text-sm text-[#667788]">
          Keep the shipment status current — the buyer sees each stage, your carrier and tracking
          number in their portal and receives an email on every change. Orders managed by ADIT are
          maintained by the ADIT team.
        </p>
      </Card>
      {orders.map((o) => (
        <Card key={o.id} data-testid={`supplier-order-${o.order_number}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <p className="font-display text-lg font-black tracking-tight text-[#102A43]">
                {o.order_number}
              </p>
              <p className="mt-1 text-sm text-[#667788]">
                {o.buyer_company_name} · {o.part_number} · enquiry {o.rfq_number}
              </p>
              <p className="mt-1 text-xs text-[#8598ac]">
                {o.carrier || "No carrier"} · {o.tracking_no || "No tracking number"}
                {o.est_delivery ? ` · ETA ${o.est_delivery}` : ""}
              </p>
              <p className="mt-1.5 text-sm font-semibold text-[#334e68]">
                <Amount value={o.total_landed_cost} currency={o.currency}
                  testId={`supplier-order-value-${o.order_number}`} />
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Pill tone={STAGE_TONE[o.stage] || "neutral"} testId={`supplier-order-stage-${o.order_number}`}>
                {o.stage_label}
              </Pill>
              {o.route === "managed" ? (
                <span className="text-xs text-[#8598ac]">Managed by ADIT</span>
              ) : (
                <Btn variant="ghost" className="px-4 py-2 text-xs"
                  data-testid={`update-tracking-${o.order_number}`}
                  onClick={() => setEdit({ ...o, note: "" })}>
                  Update shipment
                </Btn>
              )}
            </div>
          </div>
          <Timeline steps={o.timeline || []} />
        </Card>
      ))}

      {edit && (
        <Modal title={`Update ${edit.order_number}`} onClose={() => setEdit(null)}>
          <div className="grid gap-3">
            <Field label="Shipment stage">
              <Select data-testid="tracking-stage-select" value={edit.stage}
                options={STAGE_OPTIONS}
                onChange={(e) => setEdit({ ...edit, stage: e.target.value })} />
            </Field>
            <Field label="Carrier">
              <Input data-testid="tracking-carrier-input" value={edit.carrier || ""}
                placeholder="DHL Express"
                onChange={(e) => setEdit({ ...edit, carrier: e.target.value })} />
            </Field>
            <Field label="Tracking number">
              <Input data-testid="tracking-number-input" value={edit.tracking_no || ""}
                placeholder="TRK1234567"
                onChange={(e) => setEdit({ ...edit, tracking_no: e.target.value })} />
            </Field>
            <Field label="Estimated delivery">
              <Input data-testid="tracking-eta-input" type="date" value={edit.est_delivery || ""}
                onChange={(e) => setEdit({ ...edit, est_delivery: e.target.value })} />
            </Field>
            <Field label="Note for the buyer" hint="Optional — shown on the buyer's timeline.">
              <Input data-testid="tracking-note-input" value={edit.note || ""}
                onChange={(e) => setEdit({ ...edit, note: e.target.value })} />
            </Field>
          </div>
          <div className="mt-5 flex gap-3">
            <Btn data-testid="save-tracking-btn" loading={busy} onClick={save}>Save update</Btn>
            <Btn variant="ghost" data-testid="cancel-tracking-btn" onClick={() => setEdit(null)}>
              Cancel
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
